import { type ReactNode } from 'react'
import { Icon } from '#app/components/ui/icon'
import { cn } from '#app/utils/misc'

export type Faq = { q: string; a: string }

/**
 * Shared FAQ accordion for marketing pages. Native `<details>`/`<summary>` so it
 * works with no JS (CSP-safe) and the open/close is real browser behavior. The
 * `+` glyph rotates to an `×` on open. The first item is open by default.
 *
 * Pair the SAME `faqs` array with `faqPageJsonLd(faqs)` (app/utils/seo.ts) in the
 * route's meta so the visible copy and the FAQPage rich result never drift.
 */
export function FaqAccordion({
	faqs,
	className,
	defaultOpenFirst = true,
}: {
	faqs: ReadonlyArray<Faq>
	className?: string
	defaultOpenFirst?: boolean
}) {
	return (
		<div className={cn('border-hairline border-t', className)}>
			{faqs.map(({ q, a }, i) => (
				<details
					key={q}
					open={defaultOpenFirst && i === 0}
					className="group border-hairline border-b"
				>
					<summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 [&::-webkit-details-marker]:hidden">
						<span className="font-display text-ink group-hover:text-brand text-[16.5px] font-semibold transition-colors">
							{q}
						</span>
						<span className="border-hairline text-brand grid size-7 shrink-0 place-items-center rounded-full border transition-transform duration-200 group-open:rotate-45">
							<Icon name="plus" className="size-3.5" />
						</span>
					</summary>
					<p className="max-w-[640px] pb-5 text-[15px] leading-relaxed text-[#57565E]">
						{a}
					</p>
				</details>
			))}
		</div>
	)
}

/**
 * A two-column FAQ block: a sticky-ish heading/aside column on the left and the
 * accordion on the right. This is the full section shell used on the checker and
 * pricing pages. Pass your own kicker + heading via `aside`.
 */
export function FaqSection({
	faqs,
	aside,
	className,
	...rest
}: {
	faqs: ReadonlyArray<Faq>
	aside: ReactNode
	className?: string
} & React.HTMLAttributes<HTMLElement>) {
	return (
		<section
			className={cn(
				'grid gap-10 lg:grid-cols-[0.62fr_1.38fr] lg:gap-14',
				className,
			)}
			aria-label="Frequently asked questions"
			{...rest}
		>
			<div>{aside}</div>
			<FaqAccordion faqs={faqs} />
		</section>
	)
}
